// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * cm-coding: Administrators compare monthly processing metrics with the Costs
 * table during the same operational review. Use the same CSS-grid column
 * widths, pinned total, and current-month highlight so a month remains in the
 * same visual position when users move between dashboards.
 */
import { useMemo } from 'react'
import { Loader2 } from 'lucide-react'
import type { MonthlyMetricsResponse } from '../types'

const GRID = 'grid grid-cols-[230px_repeat(12,minmax(0,1fr))_110px] px-2'
const CURRENT_TINT = 'rgba(37,99,235,0.045)'

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
]

const formatNumber = (value: number) =>
  new Intl.NumberFormat('en-US').format(value)

interface MetricsTableProps {
  metrics: MonthlyMetricsResponse
  isLoading: boolean
  year: number
  /** 1-based month to highlight, only set when viewing the current calendar year */
  currentMonth: number | null
}

export function MetricsTable({ metrics, isLoading, year, currentMonth }: MetricsTableProps) {
  // Transform data into rows: one row per metric type, columns for each month
  const tableData = useMemo(() => {
    return Object.keys(metrics).map((metricType) => {
      const rollups = Array.isArray(metrics[metricType]) ? metrics[metricType] : []
      const monthlyData: Record<number, { runs: number; pages: number }> = {}

      for (let m = 1; m <= 12; m++) {
        monthlyData[m] = { runs: 0, pages: 0 }
      }
      rollups.forEach((r) => {
        if (r.month !== null) {
          monthlyData[r.month] = { runs: r.runCount, pages: r.uniqueValue }
        }
      })

      return {
        metricType,
        displayName: rollups[0]?.metricDisplayName || metricType,
        monthlyData,
        totalRuns: Object.values(monthlyData).reduce((s, d) => s + d.runs, 0),
        totalPages: Object.values(monthlyData).reduce((s, d) => s + d.pages, 0),
      }
    })
  }, [metrics])

  const tint = (month: number) =>
    month === currentMonth && tableData.length > 0 ? { background: CURRENT_TINT } : undefined

  return (
    <div className="mt-4 overflow-hidden rounded-xl border border-[#e7ecf2] bg-white shadow-[0_1px_2px_rgba(2,8,23,0.04)]">
      <div className="flex items-center justify-between border-b border-[#eef2f6] px-6 py-4">
        <div className="text-sm font-semibold tracking-[-0.01em]">Monthly breakdown</div>
        <div className="text-xs text-slate-400">Runs · pages per month</div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center gap-2 px-6 py-12">
          <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
          <span className="text-[13px] text-slate-500">Loading metrics…</span>
        </div>
      ) : tableData.length === 0 ? (
        <div className="px-6 py-12 text-center">
          <div className="text-[13.5px] font-[550] text-slate-700">No metrics data for {year}</div>
          <div className="mt-1 text-[12.5px] text-slate-400">
            Metrics will appear here once pipelines run in this year.
          </div>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <div className="min-w-[1100px] tabular-nums">
            <div className={`${GRID} items-center border-b border-[#eef2f6] bg-[#fafbfc]`}>
              <div className="px-4 py-2.5 text-[11.5px] font-semibold text-slate-500">Metric</div>
              {MONTHS.map((label, i) => (
                <div
                  key={label}
                  className={`px-2.5 py-2.5 text-right text-[11.5px] font-semibold ${
                    i + 1 === currentMonth ? 'text-[#2563eb]' : 'text-slate-500'
                  }`}
                >
                  {label}
                </div>
              ))}
              <div className="flex h-full items-center justify-end bg-[#f4f6f9] px-4 text-[11.5px] font-semibold text-slate-700">
                Total
              </div>
            </div>

            {tableData.map((row) => (
              <div
                key={row.metricType}
                className={`${GRID} items-stretch border-b border-slate-100 transition-colors duration-100 last:border-b-0 hover:bg-slate-50`}
              >
                <div className="flex min-w-0 items-center px-4 py-[11px]">
                  <span className="truncate text-[12.5px] font-[550] text-slate-800">{row.displayName}</span>
                </div>
                {MONTHS.map((_, i) => {
                  const data = row.monthlyData[i + 1]
                  return (
                    <div
                      key={i}
                      style={tint(i + 1)}
                      className="flex flex-col items-end justify-center px-2.5 py-[11px]"
                    >
                      {data.runs > 0 ? (
                        <>
                          <span className="text-[12.5px] font-[550] text-slate-800">{formatNumber(data.runs)}</span>
                          <span className="text-[10.5px] text-slate-400">{formatNumber(data.pages)} pg</span>
                        </>
                      ) : (
                        <span className="text-[12.5px] text-slate-300">—</span>
                      )}
                    </div>
                  )
                })}
                <div className="flex flex-col items-end justify-center bg-slate-50 px-4 py-[11px]">
                  <span className="text-[12.5px] font-semibold text-slate-900">{formatNumber(row.totalRuns)}</span>
                  <span className="text-[10.5px] text-slate-400">{formatNumber(row.totalPages)} pg</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
